import { useState, useEffect } from 'react'
import {
    Box,
    Paper,
    Typography,
    Button,
    Switch,
    FormControlLabel,
    Alert,
    CircularProgress,
    IconButton,
    TextField,
    InputAdornment,
    Divider,
    Chip,
    Checkbox,
    FormGroup,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Grid,
    Tooltip,
} from '@mui/material'
import {
    ContentCopy,
    Refresh,
    Api,
    CheckCircle,
    Warning,
    Delete,
    Code,
    Visibility,
    VisibilityOff,
    BarChart,
} from '@mui/icons-material'
import { formApiSettingsApi } from '@api/formApiSettings'
import { toast } from 'react-toastify'

/**
 * Form API Settings Component
 * Used within Form Settings dialog to configure external API access
 */
const FormApiSettings = ({ formId, formFields = [], language }) => {
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [confirmDialog, setConfirmDialog] = useState(null)
    const [showApiKey, setShowApiKey] = useState(false)

    const [settings, setSettings] = useState({
        is_active: false,
        api_key: null,
        visible_fields: null, // null means all fields
        rate_limit: 100,
        ip_whitelist: [],
        include_files: true,
        request_count: 0,
        last_used_at: null,
    })

    const [allFields, setAllFields] = useState(true)
    const [selectedFields, setSelectedFields] = useState([])
    const [ipWhitelistText, setIpWhitelistText] = useState('')
    const [apiBaseUrl, setApiBaseUrl] = useState('')

    // Load settings on mount
    useEffect(() => {
        if (formId) {
            loadSettings()
        }
    }, [formId])

    const loadSettings = async () => {
        try {
            setLoading(true)
            const response = await formApiSettingsApi.getSettings(formId)
            if (response.success) {
                const data = response.data
                setApiBaseUrl(data.api_base_url || `${window.location.origin}/api/external`)

                if (data.settings) {
                    setSettings(data.settings)

                    // Handle visible_fields
                    if (data.settings.visible_fields === null) {
                        setAllFields(true)
                        setSelectedFields([])
                    } else {
                        setAllFields(false)
                        setSelectedFields(data.settings.visible_fields || [])
                    }

                    // Handle IP whitelist
                    if (data.settings.ip_whitelist && Array.isArray(data.settings.ip_whitelist)) {
                        setIpWhitelistText(data.settings.ip_whitelist.join('\n'))
                    }
                }
            }
        } catch (error) {
            console.log('No API settings found for this form')
        } finally {
            setLoading(false)
        }
    }

    const handleSave = async () => {
        try {
            setSaving(true)

            // Parse IP whitelist
            const ipWhitelist = ipWhitelistText
                .split('\n')
                .map(ip => ip.trim())
                .filter(ip => ip.length > 0)

            const payload = {
                is_active: settings.is_active,
                visible_fields: allFields ? null : selectedFields,
                rate_limit: settings.rate_limit,
                ip_whitelist: ipWhitelist.length > 0 ? ipWhitelist : null,
                include_files: settings.include_files,
            }

            const response = await formApiSettingsApi.updateSettings(formId, payload)
            if (response.success) {
                toast.success(language === 'ar' ? 'تم حفظ إعدادات API' : 'API settings saved')
                setSettings(prev => ({
                    ...prev,
                    api_key: response.data.api_key || prev.api_key,
                    is_active: response.data.is_active,
                }))
            } else {
                toast.error(response.message || 'Failed to save')
            }
        } catch (error) {
            toast.error(error.message || 'Error saving settings')
        } finally {
            setSaving(false)
        }
    }

    const handleRegenerateKey = async () => {
        try {
            const response = await formApiSettingsApi.regenerateKey(formId)
            if (response.success) {
                toast.success(language === 'ar' ? 'تم إنشاء مفتاح API جديد' : 'New API key generated')
                setSettings(prev => ({
                    ...prev,
                    api_key: response.data.api_key,
                }))
            }
        } catch (error) {
            toast.error(error.message || 'Error regenerating key')
        }
        setConfirmDialog(null)
    }

    const handleRevokeKey = async () => {
        try {
            await formApiSettingsApi.revokeKey(formId)
            toast.success(language === 'ar' ? 'تم إلغاء الوصول للـ API' : 'API access revoked')
            setSettings({
                is_active: false,
                api_key: null,
                visible_fields: null,
                rate_limit: 100,
                ip_whitelist: [],
                include_files: true,
                request_count: 0,
                last_used_at: null,
            })
            setAllFields(true)
            setSelectedFields([])
            setIpWhitelistText('')
        } catch (error) {
            toast.error(error.message || 'Error revoking access')
        }
        setConfirmDialog(null)
    }

    const handleCopyApiKey = () => {
        if (settings.api_key) {
            navigator.clipboard.writeText(settings.api_key)
            toast.success(language === 'ar' ? 'تم نسخ مفتاح API' : 'API key copied')
        }
    }

    const handleCopyEndpoint = (endpoint) => {
        navigator.clipboard.writeText(endpoint)
        toast.success(language === 'ar' ? 'تم نسخ الرابط' : 'URL copied')
    }

    const toggleFieldSelection = (fieldId) => {
        setSelectedFields(prev =>
            prev.includes(fieldId)
                ? prev.filter(id => id !== fieldId)
                : [...prev, fieldId]
        )
    }

    const handleSelectAllFields = () => {
        if (selectedFields.length === formFields.length) {
            setSelectedFields([])
        } else {
            setSelectedFields(formFields.map(f => f.id))
        }
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        )
    }

    const isRTL = language === 'ar'
    const hasApiKey = !!settings.api_key

    // Generate endpoint URLs
    const submissionsEndpoint = hasApiKey
        ? `${apiBaseUrl}/${settings.api_key}/submissions`
        : `${apiBaseUrl}/{API_KEY}/submissions`

    const formInfoEndpoint = hasApiKey
        ? `${apiBaseUrl}/${settings.api_key}/form`
        : `${apiBaseUrl}/{API_KEY}/form`

    return (
        <Box sx={{ pt: 1 }} dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Info Alert */}
            <Alert severity="info" sx={{ mb: 3 }} icon={<Api />}>
                {language === 'ar'
                    ? 'قم بتمكين الوصول للـ API للسماح للمنصات الخارجية بالوصول إلى بيانات الطلبات.'
                    : 'Enable API access to allow external platforms to access submission data.'}
            </Alert>

            {/* Enable Toggle */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={settings.is_active}
                            onChange={(e) => setSettings(prev => ({ ...prev, is_active: e.target.checked }))}
                            color="primary"
                        />
                    }
                    label={
                        <Typography fontWeight={600}>
                            {language === 'ar' ? 'تفعيل الوصول للـ API' : 'Enable API Access'}
                        </Typography>
                    }
                />
                {settings.request_count > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip
                            size="small"
                            icon={<BarChart />}
                            label={`${settings.request_count} ${language === 'ar' ? 'طلب' : 'requests'}`}
                        />
                        {settings.last_used_at && (
                            <Typography variant="caption" color="text.secondary">
                                {language === 'ar' ? 'آخر استخدام:' : 'Last used:'} {new Date(settings.last_used_at).toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                )}
            </Paper>

            {/* API Key Section */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                {language === 'ar' ? 'مفتاح API' : 'API Key'}
            </Typography>

            {hasApiKey ? (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <TextField
                        fullWidth
                        label={language === 'ar' ? 'مفتاح API' : 'API Key'}
                        value={showApiKey ? settings.api_key : '••••••••••••••••••••••••••••••••'}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowApiKey(!showApiKey)} edge="end">
                                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                    <IconButton onClick={handleCopyApiKey} edge="end">
                                        <ContentCopy />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            size="small"
                            startIcon={<Refresh />}
                            onClick={() => setConfirmDialog('regenerate')}
                            color="warning"
                        >
                            {language === 'ar' ? 'إعادة إنشاء المفتاح' : 'Regenerate Key'}
                        </Button>
                        <Button
                            size="small"
                            startIcon={<Delete />}
                            onClick={() => setConfirmDialog('revoke')}
                            color="error"
                        >
                            {language === 'ar' ? 'إلغاء الوصول' : 'Revoke Access'}
                        </Button>
                    </Box>
                </Paper>
            ) : (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {language === 'ar'
                        ? 'لم يتم إنشاء مفتاح API بعد. احفظ الإعدادات لإنشاء مفتاح جديد.'
                        : 'No API key generated yet. Save settings to generate a new key.'}
                </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Field Visibility Section */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                {language === 'ar' ? 'الحقول المتاحة في الـ API' : 'Fields Available in API'}
            </Typography>

            <Paper sx={{ p: 2, mb: 3 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={allFields}
                            onChange={(e) => setAllFields(e.target.checked)}
                        />
                    }
                    label={language === 'ar' ? 'تضمين جميع الحقول' : 'Include all fields'}
                />

                {!allFields && (
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {language === 'ar' ? 'اختر الحقول للتضمين:' : 'Select fields to include:'}
                            </Typography>
                            <Button size="small" onClick={handleSelectAllFields}>
                                {selectedFields.length === formFields.length
                                    ? (language === 'ar' ? 'إلغاء الكل' : 'Deselect All')
                                    : (language === 'ar' ? 'تحديد الكل' : 'Select All')}
                            </Button>
                        </Box>
                        <FormGroup sx={{ maxHeight: 200, overflowY: 'auto' }}>
                            {formFields.filter(f => f.type !== 'captcha').map((field) => (
                                <FormControlLabel
                                    key={field.id}
                                    control={
                                        <Checkbox
                                            checked={selectedFields.includes(field.id)}
                                            onChange={() => toggleFieldSelection(field.id)}
                                            size="small"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body2">
                                                {language === 'ar' ? field.label_ar || field.label_en : field.label_en || field.label_ar}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {field.id} ({field.type})
                                            </Typography>
                                        </Box>
                                    }
                                />
                            ))}
                        </FormGroup>
                    </Box>
                )}

                {/* Include Files Toggle */}
                <Divider sx={{ my: 2 }} />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={settings.include_files}
                            onChange={(e) => setSettings(prev => ({ ...prev, include_files: e.target.checked }))}
                        />
                    }
                    label={language === 'ar' ? 'تضمين روابط الملفات المرفقة' : 'Include file attachment URLs'}
                />
            </Paper>

            {/* Rate Limiting Section */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                {language === 'ar' ? 'إعدادات متقدمة' : 'Advanced Settings'}
            </Typography>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label={language === 'ar' ? 'الحد الأقصى للطلبات/دقيقة' : 'Rate Limit (requests/minute)'}
                            value={settings.rate_limit}
                            onChange={(e) => setSettings(prev => ({
                                ...prev,
                                rate_limit: Math.max(1, Math.min(1000, parseInt(e.target.value) || 100))
                            }))}
                            helperText={language === 'ar' ? 'الحد الأقصى: 1000' : 'Maximum: 1000'}
                            inputProps={{ min: 1, max: 1000 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={language === 'ar' ? 'قائمة IPs المسموحة (اختياري)' : 'IP Whitelist (Optional)'}
                            value={ipWhitelistText}
                            onChange={(e) => setIpWhitelistText(e.target.value)}
                            placeholder="192.168.1.1&#10;10.0.0.1"
                            helperText={language === 'ar'
                                ? 'عناوين IP مسموحة (عنوان واحد لكل سطر). اتركه فارغاً للسماح بالكل.'
                                : 'Allowed IP addresses (one per line). Leave empty to allow all.'}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* API Documentation */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                {language === 'ar' ? 'وثائق الـ API' : 'API Documentation'}
            </Typography>

            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    {language === 'ar' ? 'نقاط النهاية المتاحة:' : 'Available Endpoints:'}
                </Typography>

                {/* GET Submissions */}
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip label="GET" size="small" color="primary" />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>
                            {submissionsEndpoint}
                        </Typography>
                        {hasApiKey && (
                            <IconButton size="small" onClick={() => handleCopyEndpoint(submissionsEndpoint)}>
                                <ContentCopy fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {language === 'ar' ? 'جلب جميع الطلبات مع التصفية والتقسيم' : 'Fetch all submissions with pagination & filtering'}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        Params: ?page=1&limit=50&status=approved&from=2026-01-01&to=2026-02-01
                    </Typography>
                </Box>

                {/* GET Single Submission */}
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip label="GET" size="small" color="primary" />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {submissionsEndpoint}/{'{submission_id}'}
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {language === 'ar' ? 'جلب طلب واحد بالمعرف' : 'Fetch single submission by ID'}
                    </Typography>
                </Box>

                {/* GET Form Info */}
                <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip label="GET" size="small" color="primary" />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>
                            {formInfoEndpoint}
                        </Typography>
                        {hasApiKey && (
                            <IconButton size="small" onClick={() => handleCopyEndpoint(formInfoEndpoint)}>
                                <ContentCopy fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {language === 'ar' ? 'جلب معلومات النموذج وبنية الحقول' : 'Fetch form metadata and field structure'}
                    </Typography>
                </Box>
            </Paper>

            {/* Save Button */}
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} /> : <CheckCircle />}
                >
                    {language === 'ar' ? 'حفظ الإعدادات' : 'Save Settings'}
                </Button>
            </Box>

            {/* Confirmation Dialogs */}
            <Dialog open={confirmDialog === 'regenerate'} onClose={() => setConfirmDialog(null)}>
                <DialogTitle>
                    {language === 'ar' ? 'إعادة إنشاء مفتاح API؟' : 'Regenerate API Key?'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {language === 'ar'
                            ? 'سيتم إبطال المفتاح القديم فوراً. ستحتاج المنصات الخارجية إلى تحديث المفتاح.'
                            : 'The old key will be invalidated immediately. External platforms will need to update the key.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog(null)}>
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button onClick={handleRegenerateKey} color="warning" variant="contained">
                        {language === 'ar' ? 'إعادة إنشاء' : 'Regenerate'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDialog === 'revoke'} onClose={() => setConfirmDialog(null)}>
                <DialogTitle>
                    {language === 'ar' ? 'إلغاء الوصول للـ API؟' : 'Revoke API Access?'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {language === 'ar'
                            ? 'سيتم حذف مفتاح API ولن تتمكن المنصات الخارجية من الوصول للبيانات.'
                            : 'This will delete the API key. External platforms will no longer be able to access data.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog(null)}>
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button onClick={handleRevokeKey} color="error" variant="contained">
                        {language === 'ar' ? 'إلغاء الوصول' : 'Revoke Access'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default FormApiSettings
